import React, { FC } from 'react'
import styles from './SearchComponents.module.scss'
import SearchIcon from 'svg/SA/search-small.svg'
import RecentSearchIcon from 'svg/SA/recent-searches-os.svg'
import SALink from 'components/SALink'
import { OptionObject, SearchOptionLinksProps } from './SearchOptionLinks'
import useSearchProvider from 'components/SA/SearchProvider/hooks/useSearchProvider'

type HighlightMatchTextProps = {
  text: string
  matchedTerm?: string
  highlightMatchedTerms?: boolean
}

const HighlightMatchText: React.FC<HighlightMatchTextProps> = ({
  text,
  matchedTerm,
  highlightMatchedTerms,
}) => {
  if (!matchedTerm || !highlightMatchedTerms) {
    return <span className={styles.rowLinkTextSpan}>{text}</span>
  }

  const escapedTerm = matchedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Escape RegExp special characters
  const regex = new RegExp(`(${escapedTerm})`, 'gi') // global, case-insensitive
  const textMatchedTermsArray = text.split(regex)

  return (
    <span className={styles.rowTextsBox}>
      {textMatchedTermsArray.map((textString, index) =>
        regex.test(textString) ? (
          <strong key={index} className={styles.rowMatchedText}>
            {textString}
          </strong>
        ) : (
          <span key={index} className={styles.rowLinkTextSpan}>
            {textString}
          </span>
        )
      )}
    </span>
  )
}

type OptionRowLinkProps = OptionObject &
  Pick<
    SearchOptionLinksProps,
    'setIsFocused' | 'onSearch' | 'matchedTerm' | 'highlightMatchedTerms' | 'icon' | 'sectionName'
  >

const OptionRowLink: FC<OptionRowLinkProps> = ({
  label,
  link,
  setIsFocused,
  onSearch,
  matchedTerm,
  highlightMatchedTerms,
  icon = 'search',
  sectionName,
}) => {
  const { getSearchProviderAttributes } = useSearchProvider()

  const handleSearchOptionClick = (event: React.MouseEvent<HTMLAnchorElement>, query: string) => {
    event.preventDefault()
    onSearch(query)
    setIsFocused(false)
  }

  return (
    <SALink
      className={styles.searchOptionRow}
      href={link}
      title={label}
      onClick={(e) => handleSearchOptionClick(e, label)}
      {...(!!sectionName ? getSearchProviderAttributes([{ 'item-name': label }]) : {})}
      {...(!!sectionName ? getSearchProviderAttributes([{ 'item-section': sectionName }]) : {})}
    >
      {icon === 'search' ? <SearchIcon /> : <RecentSearchIcon />}
      <HighlightMatchText
        text={label}
        matchedTerm={matchedTerm}
        highlightMatchedTerms={highlightMatchedTerms}
      />
    </SALink>
  )
}

export default OptionRowLink
